import _styled from '@emotion/styled/base';
import { IconChevronDownSmall, IconClearFilled } from '@hubble/icons/web';
import { Flex } from '../../Flex/index.js';
import { shorthandSpacingCss, Spacer } from '../../primitives/Spacer/index.js';
import { getSize, FORM_ELEMENT_TRANSITION_VALUE, getFontSize } from '../shared/utils.js';
import { Spacing } from '../../primitives/spacing.js';
import { getColor } from '../../utils/themeUtils.js';
import { Colors } from '../../primitives/color.js';
import { border } from '@hubble/tokens/web';

function getIconSize(size) {
  if (size === 'sm') return Spacing.scale[2];
  if (size === 'md') return Spacing.scale[2.5];
  if (size === 'lg') return Spacing.scale[3];
  return Spacing.scale[2];
}
function getControlBgColor(isDisabled, error) {
  if (isDisabled) return getColor(Colors.gray[50], Colors.gray[900]);
  if (error) return getColor(Colors.red[50], Colors.red[800]);
  return getColor(Colors.white, Colors.black);
}
function getOptionBgColor(isSelected, isFocused) {
  if (isSelected) return getColor(Colors.gray[100], Colors.gray[600]);
  if (isFocused) return getColor(Colors.gray[50], Colors.gray[700]);
  return getColor(Colors.white, Colors.gray[800]);
}
function getControlBorderColor(error, isFocused, isDisabled) {
  if (error) return getColor(Colors.red[600], Colors.red[400]);
  if (isFocused) return getColor(Colors.black, Colors.white);
  if (isDisabled) return getColor(Colors.gray[100], Colors.gray[800]);
  return getColor(Colors.gray[200], Colors.gray[700]);
}
function getTextStyles(theme, provided = {}, size) {
  return {
    ...provided,
    alignItems: 'center',
    color: getColor(Colors.black, Colors.white)({
      theme
    }),
    fontSize: getFontSize(size),
    lineHeight: '20px'
  };
}
const OptionLabel = _styled("div", {
  target: "ew5wvmf7"
})({
  name: "7nhebw",
  styles: "display:grid;grid-template-areas:'icon content';grid-template-columns:min-content 1fr"
} );
const OptionTextWrapper = _styled(Flex, {
  target: "ew5wvmf6"
})({
  name: "1sxg93g",
  styles: "grid-area:content"
} );
const OptionIconWrapper = _styled("div", {
  target: "ew5wvmf5"
})("align-items:center;display:flex;grid-area:icon;margin:0 ", Spacing.scale[2], " 0 0;width:max-content;max-width:", Spacing.scale[4], ";");
const SelectWrapper = _styled("div", {
  target: "ew5wvmf4"
})("width:100%;", OptionIconWrapper, ">svg{width:", ({
  size
}) => size === 'lg' ? Spacing.scale[4] : Spacing.scale[3], ";height:", ({
  size
}) => size === 'lg' ? Spacing.scale[4] : Spacing.scale[3], ";}", shorthandSpacingCss, ";");
const IndicatorIcon = _styled(IconChevronDownSmall, {
  target: "ew5wvmf3"
})(({
  size
}) => `
    width: ${getIconSize(size)};
    height: ${getIconSize(size)};
  `, " ", ({
  disabled,
  theme
}) => disabled && `fill: ${getColor(Colors.gray[300], Colors.gray[600])({
  theme
})};`, ";");
const SubLabel = _styled(Spacer, {
  target: "ew5wvmf2"
})("font-size:12px;line-height:16px;color:", getColor(Colors.gray[600], Colors.gray[400]), ";");
const ClearIndicator = _styled(IconClearFilled, {
  target: "ew5wvmf1"
})("border-radius:", border.radius.full, ";margin-right:", Spacing.scale[1], ";height:", ({
  size
}) => getIconSize(size), ";width:", ({
  size
}) => getIconSize(size), ";path{fill:", ({
  theme
}) => theme.colorScheme.content.secondary, ";}&:hover path{fill:", ({
  theme
}) => theme.colorScheme.content.primary, ";}");
const MultiValueRemove = _styled(IconClearFilled, {
  target: "ew5wvmf0"
})("height:", Spacing.scale[2], ";margin-left:", Spacing.scale[0.5], ";width:", Spacing.scale[2], ";fill:", getColor(Colors.gray[600], Colors.gray[400]), ";");
const selectStyles = theme => ({
  control: (provided, state) => {
    const {
      isFocused,
      isDisabled,
      selectProps
    } = state;
    const {
      error,
      size
    } = selectProps;
    const borderColor = getControlBorderColor(error, isFocused, isDisabled)({
      theme
    });
    return {
      ...getTextStyles(theme, provided, size),
      backgroundColor: getControlBgColor(isDisabled, error)({
        theme
      }),
      border: `1px solid ${borderColor}`,
      borderRadius: border.radius.md,
      boxShadow: `${isFocused && `0 0 0 1px ${borderColor}`}`,
      color: getColor(Colors.black, Colors.white)({
        theme
      }),
      cursor: 'pointer',
      fontWeight: '400',
      minHeight: getSize(size),
      transition: FORM_ELEMENT_TRANSITION_VALUE,
      '&:hover': {
        borderColor: !isFocused && !error && getColor(Colors.gray[300], Colors.gray[600])({
          theme
        })
      }
    };
  },
  dropdownIndicator: () => ({
    display: 'flex',
    paddingRight: Spacing.scale[2]
  }),
  group: () => ({
    padding: 0
  }),
  groupHeading: () => ({
    fontSize: '12px',
    fontWeight: 600,
    margin: `${Spacing.scale[2]} ${Spacing.scale[1]} ${Spacing.scale[1.5]}`
  }),
  indicatorsContainer: (provided, state) => {
    const {
      selectProps: {
        readOnly
      }
    } = state;
    return {
      ...provided,
      ...(readOnly && {
        display: 'none'
      })
    };
  },
  indicatorSeparator: () => ({
    display: 'none'
  }),
  input: (provided, state) => {
    const {
      selectProps
    } = state;
    const {
      size
    } = selectProps;
    return {
      ...getTextStyles(theme, provided, size),
      display: 'inline-grid',
      marginLeft: Spacing.scale[1]
    };
  },
  loadingMessage: (provided, state) => {
    const {
      selectProps
    } = state;
    const {
      size
    } = selectProps;
    return {
      ...getTextStyles(theme, provided, size),
      color: Colors.gray[200]
    };
  },
  menuPortal: provided => {
    return {
      ...provided,
      zIndex: 9999,
      pointerEvents: 'auto'
    };
  },
  menu: provided => {
    return {
      ...provided,
      backgroundColor: getColor(Colors.white, Colors.gray[800])({
        theme
      }),
      borderRadius: border.radius.md,
      color: getColor(Colors.black, Colors.white)({
        theme
      }),
      marginTop: '4px',
      padding: '0'
    };
  },
  menuList: provided => ({
    ...provided,
    padding: `0 ${Spacing.scale[1]}`
  }),
  multiValue: provided => {
    return {
      ...provided,
      alignItems: 'center',
      backgroundColor: getColor(Colors.gray[100], Colors.gray[800])({
        theme
      }),
      borderRadius: border.radius.full,
      display: 'flex',
      fontWeight: 600,
      lineHeight: 1,
      marginLeft: Spacing.scale[0.5],
      padding: Spacing.scale[0.5],
      '&:last-child': {
        marginLeft: 0
      }
    };
  },
  multiValueLabel: (provided, state) => {
    const {
      isDisabled
    } = state;
    return {
      ...provided,
      color: isDisabled ? getColor(Colors.gray[300], Colors.gray[600])({
        theme
      }) : getColor(Colors.black, Colors.white)({
        theme
      }),
      lineHeight: 1,
      fontSize: '12px'
    };
  },
  multiValueRemove: (provided, state) => {
    const {
      isDisabled
    } = state;
    return {
      ...provided,
      path: {
        fill: isDisabled && getColor(Colors.gray[300], Colors.gray[600])({
          theme
        })
      },
      '&:hover': {
        background: 'none'
      },
      '&:hover path': {
        fill: getColor(Colors.black, Colors.white)({
          theme
        })
      }
    };
  },
  noOptionsMessage: provided => {
    return {
      ...provided,
      color: getColor(Colors.gray[600], Colors.gray[400])({
        theme
      }),
      padding: Spacing.scale[2],
      textAlign: 'left'
    };
  },
  option: (provided, state) => {
    const {
      isFocused,
      isSelected,
      isDisabled,
      data
    } = state;
    if (data.isDivider) {
      return {
        margin: `${Spacing.scale[0.5]} 0`,
        borderBottom: `1px solid ${getColor(Colors.gray[200], Colors.gray[700])({
          theme
        })}`
      };
    }
    return {
      ...getTextStyles(theme, provided),
      background: getOptionBgColor(isSelected, isFocused)({
        theme
      }),
      borderRadius: border.radius.md,
      color: isDisabled ? getColor(Colors.gray[300], Colors.gray[600])({
        theme
      }) : getColor(Colors.black, Colors.white)({
        theme
      }),
      cursor: isDisabled ? 'default' : 'pointer',
      fontWeight: 400,
      margin: `${Spacing.scale[1]} 0`,
      minHeight: '36px',
      padding: Spacing.scale[1],
      '&:hover': {
        backgroundColor: getColor(Colors.gray[50], Colors.gray[700])({
          theme
        })
      }
    };
  },
  placeholder: (provided, state) => {
    const {
      isDisabled
    } = state;
    return {
      ...provided,
      color: isDisabled ? Colors.gray[500] : getColor(Colors.gray[600], Colors.gray[400])({
        theme
      }),
      fontWeight: 400,
      paddingLeft: Spacing.scale[1]
    };
  },
  singleValue: (provided, state) => {
    const {
      selectProps
    } = state;
    const {
      size
    } = selectProps;
    return {
      ...getTextStyles(theme, provided, size),
      paddingLeft: Spacing.scale[1],
      '.form-sublabel': {
        display: 'none'
      }
    };
  },
  valueContainer: (provided, state) => {
    const {
      selectProps
    } = state;
    const {
      size
    } = selectProps;
    return {
      ...provided,
      minWidth: 0,
      padding: `0 ${Spacing.scale[0.5]}`,
      rowGap: size === 'sm' ? Spacing.scale[0.25] : Spacing.scale[0.5]
    };
  }
});

export { ClearIndicator, IndicatorIcon, MultiValueRemove, OptionIconWrapper, OptionLabel, OptionTextWrapper, SelectWrapper, SubLabel, selectStyles };
